import { Inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, Subject, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { API_BASE_URL } from "src/swagger/api.client.generated";
import {
  BellBiteItem,
  BellCommunityItem,
  BellPlanItem,
  BellScoreItem,
  EMPTY_NOTIFICATION_BELL,
  NotificationBell,
} from "./dialog-notifications.model";
import { SettingsLocalService } from "../../pages/settings/settings.service";

interface Envelope<T> {
  resultStatus?: { statusCode?: number; errorText?: string };
  responseData?: T | null;
}

/** How often the bell is refreshed while the app is visible. Matches the server scheduler's cadence. */
export const NOTIFICATION_POLL_INTERVAL_MS = 60_000;

/**
 * The in-app bell and modal (FLIP-467).
 *
 * The server is the single source of truth: it builds the bell from the notification log, so the same
 * entries show on every device and survive reload, device change and reinstall. This service only holds
 * the last response and tells the bell when it changes. It no longer schedules cards with timers from
 * the settings' next-alert times, and it keeps no unseen state in localStorage.
 *
 * The bell is independent of push - an entry exists even with push off or no subscription - so a push
 * cannot be what triggers a refresh. Instead it refreshes at login, every minute while the tab is
 * visible, when the tab becomes visible again, and after the user acts on an entry.
 *
 * An entry stays until the user dismisses it - its X, or its action button, via {@link dismiss}. Fetching
 * (GetBell) is read-only, and opening or closing the modal dismisses nothing, nor does tapping a push.
 * (The server's MarkSeen endpoint and NotificationLogs.SeenUtc keep their names; they now mean "dismissed".)
 *
 * Uses the direct-HttpClient + API_BASE_URL pattern of FeatureFlagService rather than the NSwag client,
 * which is generated from a live API and cannot be regenerated here.
 */
@Injectable({
  providedIn: "root",
})
export class DialogNotificationService {
  private changedSource = new Subject<void>();
  /** Emits whenever the bell's contents change, so the badge and an open modal can re-render. */
  notificationChanged = this.changedSource.asObservable();

  private bell: NotificationBell = EMPTY_NOTIFICATION_BELL;

  private pollTimer: any = null;
  private knownIds = new Set<number>();
  private hasLoadedOnce = false;

  private visibilityHandler = () => {
    if (document.visibilityState === "visible") {
      this.refresh();
    }
  };

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string,
    private settingsService: SettingsLocalService
  ) {}

  /** The single Score entry, or null. */
  get scoreEntry(): BellScoreItem | null {
    return this.bell.score;
  }

  /** The single Plan entry (FLIP-474), or null. */
  get planEntry(): BellPlanItem | null {
    return this.bell.plan;
  }

  /** One entry per distinct unseen bite, newest first. */
  get biteEntries(): BellBiteItem[] {
    return this.bell.bites;
  }

  /** One entry per Community event (FLIP-470), newest first. Empty while Community is switched off server-side. */
  get communityEntries(): BellCommunityItem[] {
    return this.bell.community;
  }

  /**
   * Starts keeping the bell current: loads it now and then refreshes on a timer and whenever the tab
   * becomes visible. Call once per authenticated session; safe to call again.
   */
  init(): void {
    this.refresh();

    if (this.pollTimer === null) {
      this.pollTimer = setInterval(() => {
        if (document.visibilityState === "visible") {
          this.refresh();
        }
      }, NOTIFICATION_POLL_INTERVAL_MS);
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }
  }

  /** Stops refreshing and forgets everything - call on logout so the next account starts clean. */
  reset(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
    this.bell = EMPTY_NOTIFICATION_BELL;
    this.knownIds.clear();
    this.hasLoadedOnce = false;
    this.changedSource.next();
  }

  /** Fetches the bell and updates the badge. Read-only: fetching marks nothing seen. A failure keeps the last bell. */
  refresh(): void {
    this.fetchBell().subscribe((fresh) => {
      if (fresh) {
        this.applyBell(fresh);
      }
    });
  }

  /**
   * The total to show on the badge: the server's count - the Score entry (at most one), the Plan entry (at most
   * one), every Brain Science Bite entry and every Community entry. Community is zero until it is switched on.
   */
  getNotificationCount(): number {
    return this.bell.total;
  }

  /**
   * Called when the notifications modal opens. Fetches the current bell and returns what to display. Opening
   * the modal dismisses nothing: an entry stays until the user clicks its X or its action button, which is
   * {@link dismiss}.
   *
   * If the fetch fails the cached bell is returned.
   */
  loadForModal(): Observable<NotificationBell> {
    return this.fetchBell().pipe(
      map((fresh) => {
        if (!fresh) {
          return this.bell;
        }
        this.applyBell(fresh);
        return fresh;
      })
    );
  }

  /**
   * The user dismissed an entry - its X, or its action button. Removes it from the cached bell at once, so the
   * badge drops straight away, tells the server (one dismissal covers every log row the entry stands for, on
   * every device) and then refreshes to reconcile with the server. If the server call fails the entry is not
   * dismissed there, and the refresh brings it back - which is the true state.
   */
  dismiss(notificationLogIds: number[]): void {
    const ids = new Set(notificationLogIds ?? []);
    if (ids.size === 0) {
      return;
    }

    const without = (entry: { notificationLogIds?: number[] }) =>
      !(entry.notificationLogIds ?? []).some((id) => ids.has(id));

    const score = this.bell.score && without(this.bell.score) ? this.bell.score : null;
    const plan = this.bell.plan && without(this.bell.plan) ? this.bell.plan : null;
    const bites = this.bell.bites.filter(without);
    const community = this.bell.community.filter(without);
    this.bell = {
      total: (score ? 1 : 0) + (plan ? 1 : 0) + bites.length + community.length,
      score,
      plan,
      bites,
      community,
    };
    this.changedSource.next();

    this.postDismiss([...ids]).subscribe(() => this.refresh());
  }

  /**
   * A Community push reached this open tab (the service worker relays it - see app.component.ts's
   * COMMUNITY_NOTIFICATION handler). The entry is already on the server: it writes the log row BEFORE it sends the
   * push, and the row is the bell entry, so one refresh makes it appear at once instead of at the next poll.
   *
   * Nothing is added here from the message itself. Community cards used to be built from it and held in memory,
   * which lost them on reload, missed them when no tab was open, and let the push and the bell disagree. The
   * server decides whether there is an entry at all - Community switched off, or off for this user, means none -
   * so there is no flag or setting to check on this side.
   */
  onCommunityPushReceived(): void {
    this.refresh();
  }

  private fetchBell(): Observable<NotificationBell | null> {
    return this.http
      .get<Envelope<NotificationBell>>(`${this.apiBaseUrl}/api/Notifications/GetBell`)
      .pipe(
        map((response) => this.normalise(response?.responseData)),
        catchError(() => of(null))
      );
  }

  /** The server's MarkSeen endpoint; since entries are dismissed rather than seen, that is what it now records. */
  private postDismiss(notificationLogIds: number[]): Observable<boolean> {
    return this.http
      .post<Envelope<number>>(`${this.apiBaseUrl}/api/Notifications/MarkSeen`, { notificationLogIds })
      .pipe(
        map((response) => response?.responseData != null),
        catchError(() => of(false))
      );
  }

  private normalise(raw: NotificationBell | null | undefined): NotificationBell | null {
    if (!raw) {
      return null;
    }
    const bites = raw.bites ?? [];
    const score = raw.score ?? null;
    // An older API without Plan or Community simply has no such key.
    const plan = raw.plan ?? null;
    const community = raw.community ?? [];
    return {
      total: raw.total ?? (score ? 1 : 0) + (plan ? 1 : 0) + bites.length + community.length,
      score,
      plan,
      bites,
      community,
    };
  }

  private applyBell(fresh: NotificationBell): void {
    // A sound for an entry that appeared since the last look - the parity of the old timer firing -
    // but not for the very first load, which would chime for everything already waiting at login.
    const ids = this.idsOf(fresh);
    const appeared = ids.some((id) => !this.knownIds.has(id));
    ids.forEach((id) => this.knownIds.add(id));
    if (this.hasLoadedOnce && appeared) {
      this.playSound();
    }
    this.hasLoadedOnce = true;

    this.bell = fresh;
    this.changedSource.next();
  }

  private idsOf(bell: NotificationBell): number[] {
    // (a loop rather than flatMap: this project's TypeScript lib predates es2019)
    const ids: number[] = [...(bell.score?.notificationLogIds ?? [])];
    ids.push(...(bell.plan?.notificationLogIds ?? []));
    for (const bite of bell.bites) {
      ids.push(...(bite.notificationLogIds ?? []));
    }
    for (const event of bell.community) {
      ids.push(...(event.notificationLogIds ?? []));
    }
    return ids;
  }

  private playSound(): void {
    const settings = this.settingsService.getSettings();
    if (!settings?.audioIsOn) {
      return;
    }

    try {
      // play() returns a promise that rejects when the browser blocks autoplay; that is not an error.
      new Audio("../assets/BC_Notification.mp3").play()?.catch(() => undefined);
    } catch (error) {
      console.log(error);
    }
  }
}
